import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ProjectNote,
  ProjectNoteSlice
} from 'interfaces/project-note.interface';
import {
  fetchProjectNotes,
  fetchProjectNoteById,
  createProjectNote,
  updateProjectNote,
  deleteProjectNote,
  fetchNoteComments,
  addNoteComment,
  deleteNoteComment
} from './extra';

const initialState: ProjectNoteSlice = {
  notes: [],
  pagination: null,
  isNotesLoading: false,
  isCreatingNote: false,
  currentNote: null,
  isNoteLoading: false,
  isSavingNote: false,
  isDeletingNote: false,
  comments: [],
  isCommentsLoading: false,
  isAddingComment: false
};

const projectNoteSlice = createSlice({
  name: 'projectNote',
  initialState,
  reducers: {
    setCurrentNote(state, action: PayloadAction<ProjectNote | null>) {
      state.currentNote = action.payload;
    },
    resetProjectNotes() {
      return initialState;
    },
    clearNoteComments(state) {
      state.comments = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectNotes.pending, (state) => {
        state.isNotesLoading = true;
      })
      .addCase(fetchProjectNotes.fulfilled, (state, action) => {
        state.isNotesLoading = false;
        if (action.payload) {
          state.notes = action.payload.data;
          state.pagination = action.payload.meta;
        }
      })
      .addCase(fetchProjectNotes.rejected, (state) => {
        state.isNotesLoading = false;
      })

      .addCase(fetchProjectNoteById.pending, (state) => {
        state.isNoteLoading = true;
      })
      .addCase(fetchProjectNoteById.fulfilled, (state, action) => {
        state.isNoteLoading = false;
        state.currentNote = action.payload;
      })
      .addCase(fetchProjectNoteById.rejected, (state) => {
        state.isNoteLoading = false;
      })

      .addCase(createProjectNote.pending, (state) => {
        state.isCreatingNote = true;
      })
      .addCase(createProjectNote.fulfilled, (state, action) => {
        state.isCreatingNote = false;
        if (action.payload) {
          state.notes = [action.payload, ...state.notes];
          state.currentNote = action.payload;
        }
      })
      .addCase(createProjectNote.rejected, (state) => {
        state.isCreatingNote = false;
      })

      .addCase(updateProjectNote.pending, (state) => {
        state.isSavingNote = true;
      })
      .addCase(updateProjectNote.fulfilled, (state, action) => {
        state.isSavingNote = false;
        const updated = action.payload;
        if (updated) {
          state.currentNote = updated;
          state.notes = state.notes.map((note) =>
            note.id === updated.id
              ? {
                  ...note,
                  title: updated.title,
                  updated_at: updated.updated_at
                }
              : note
          );
        }
      })
      .addCase(updateProjectNote.rejected, (state) => {
        state.isSavingNote = false;
      })

      .addCase(deleteProjectNote.pending, (state) => {
        state.isDeletingNote = true;
      })
      .addCase(deleteProjectNote.fulfilled, (state, action) => {
        state.isDeletingNote = false;
        const deletedId = action.payload;
        if (deletedId) {
          state.notes = state.notes.filter((note) => note.id !== deletedId);
          if (state.currentNote?.id === deletedId) {
            state.currentNote = null;
          }
        }
      })
      .addCase(deleteProjectNote.rejected, (state) => {
        state.isDeletingNote = false;
      })

      .addCase(fetchNoteComments.pending, (state) => {
        state.isCommentsLoading = true;
      })
      .addCase(fetchNoteComments.fulfilled, (state, action) => {
        state.isCommentsLoading = false;
        if (action.payload) {
          state.comments = action.payload.data;
        }
      })
      .addCase(fetchNoteComments.rejected, (state) => {
        state.isCommentsLoading = false;
      })

      .addCase(addNoteComment.pending, (state) => {
        state.isAddingComment = true;
      })
      .addCase(addNoteComment.fulfilled, (state, action) => {
        state.isAddingComment = false;
        if (action.payload) {
          state.comments = [...state.comments, action.payload];
        }
      })
      .addCase(addNoteComment.rejected, (state) => {
        state.isAddingComment = false;
      })

      .addCase(deleteNoteComment.fulfilled, (state, action) => {
        const deletedId = action.payload;
        if (deletedId) {
          state.comments = state.comments.filter(
            (comment) => comment.id !== deletedId
          );
        }
      });
  }
});

export const { setCurrentNote, resetProjectNotes, clearNoteComments } =
  projectNoteSlice.actions;

export default projectNoteSlice.reducer;
